/**
 * The theme preference, and how it becomes a palette.
 *
 * Three values: "light", "dark", and "system" — the default, following the operating
 * system. Only the resolved answer reaches the DOM, as `data-theme` on <html>, because
 * that is what the CSS variables key off.
 *
 * The same resolution runs twice: once in `public/theme.js` before the first paint, and
 * again here once React is up so a change takes effect without a reload. Keep the two in
 * step — the storage key is duplicated there deliberately, since that file cannot import.
 */

export type Theme = "light" | "dark" | "system";

export const THEME_KEY = "splitwell.theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

export function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Private browsing refuses localStorage; following the OS is the right default anyway.
  }
  return "system";
}

/** Resolve a preference to the palette actually shown. */
export function resolve(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return window.matchMedia?.(DARK_QUERY).matches ? "dark" : "light";
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = resolve(theme);
}

export function storeTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Not being able to remember the choice is survivable; failing to apply it is not.
  }
  applyTheme(theme);
}

/**
 * Re-apply when the OS flips, for as long as the preference is "system".
 *
 * Mounted once at the root rather than inside the toggle: the toggle only exists on
 * signed-in pages, so before this the OS switching to dark while someone sat on the
 * sign-in screen did nothing until they navigated.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const media = window.matchMedia?.(DARK_QUERY);
  if (!media) return () => {};
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
